import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { callAPI } from '../../../network/networkConnection';

const BASE_API = process.env.REACT_APP_APIUrl || 'http://localhost:5000/api/';

// ─── Status Config ───────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  SUCCESS: {
    icon: (
      <Typography variant="h1" sx={{ fontSize: 80, color: 'success.main', fontWeight: 700 }}>
        ✓
      </Typography>
    ),
    title: 'Payment Successful!',
    subtitle: 'Your payment has been processed. You will receive a confirmation shortly.',
    color: 'success.main',
    bg: '#f0faf4',
  },
  FAILURE: {
    icon: (
      <Typography variant="h1" sx={{ fontSize: 80, color: 'error.main', fontWeight: 700 }}>
        ✕
      </Typography>
    ),
    title: 'Payment Failed',
    subtitle: 'Your payment could not be processed. Please try again or use a different payment method.',
    color: 'error.main',
    bg: '#fff5f5',
  },
  ABORTED: {
    icon: (
      <Typography variant="h1" sx={{ fontSize: 80, color: 'warning.main', fontWeight: 700 }}>
        !
      </Typography>
    ),
    title: 'Payment Cancelled',
    subtitle: 'You cancelled the payment. No amount has been deducted from your account.',
    color: 'warning.main',
    bg: '#fffbf0',
  },
  PENDING: {
    icon: (
      <Typography variant="h1" sx={{ fontSize: 80, color: 'info.main', fontWeight: 700 }}>
        ⏳
      </Typography>
    ),
    title: 'Payment Pending',
    subtitle: 'Your payment is being processed. Please do not refresh or close this page.',
    color: 'info.main',
    bg: '#f0f8ff',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const statusParam = (searchParams.get('status') || 'PENDING').toUpperCase();
  const orderId = searchParams.get('orderId') || '';
  const urlMessage = searchParams.get('message') || '';

  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const config = STATUS_CONFIG[statusParam] || STATUS_CONFIG.PENDING;

  // Poll the backend for authoritative order details when we have an orderId
  useEffect(() => {
    if (!orderId) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 5;
    const POLL_INTERVAL_MS = 2500;

    const fetchStatus = async () => {
      if (cancelled) return;
      setLoadingOrder(true);
      try {
        const url = `${BASE_API}payment/status/${orderId}`;
        const res = await callAPI(url, 'GET', {}, null);
        if (!cancelled && res?.data) {
          setOrderDetails(res.data);
        }
      } catch (_) {
        // Non-critical; the URL param is already shown
      } finally {
        if (!cancelled) setLoadingOrder(false);
      }
    };

    fetchStatus();

    // For PENDING status, retry a few times to catch async updates
    if (statusParam === 'PENDING') {
      const interval = setInterval(() => {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          clearInterval(interval);
          return;
        }
        fetchStatus();
      }, POLL_INTERVAL_MS);

      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [orderId, statusParam]);

  const authoritative = orderDetails?.paymentStatus
    ? STATUS_CONFIG[orderDetails.paymentStatus] || config
    : config;

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          backgroundColor: authoritative.bg,
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: 2 }}>{authoritative.icon}</Box>

        {/* Title */}
        <Typography variant="h5" fontWeight={700} color={authoritative.color} gutterBottom>
          {authoritative.title}
        </Typography>

        {/* Subtitle */}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {urlMessage || authoritative.subtitle}
        </Typography>

        {/* Order Details */}
        {(orderId || orderDetails) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ textAlign: 'left', mb: 3 }}>
              {orderId && (
                <DetailRow label="Order ID" value={orderId} />
              )}
              {orderDetails?.amount !== undefined && (
                <DetailRow
                  label="Amount"
                  value={`₹${Number(orderDetails.amount).toFixed(2)} ${orderDetails.currency || 'INR'}`}
                />
              )}
              {orderDetails?.paymentMode && (
                <DetailRow label="Payment Mode" value={orderDetails.paymentMode} />
              )}
              {orderDetails?.trackingId && (
                <DetailRow label="Tracking ID" value={orderDetails.trackingId} />
              )}
              {orderDetails?.bankRefNo && (
                <DetailRow label="Bank Ref No" value={orderDetails.bankRefNo} />
              )}
              {orderDetails?.failureMessage && statusParam !== 'SUCCESS' && (
                <DetailRow label="Reason" value={orderDetails.failureMessage} />
              )}
            </Box>
          </>
        )}

        {loadingOrder && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Verifying payment status…
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {(statusParam === 'FAILURE' || statusParam === 'ABORTED') && (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/payment')}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              Try Again
            </Button>
          )}
          <Button
            variant={statusParam === 'SUCCESS' ? 'contained' : 'outlined'}
            size="large"
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Go to Home
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

// ─── Sub-component ────────────────────────────────────────────────────────────

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 0.75,
        gap: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}
